<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-ban me-2" style="color: #ef4444;"></i>{{ $t('Cancelled Orders') }}
          </h5>
          <small class="text-muted">{{ $t('Orders') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- ยกเลิกรายการอาหาร — จุดรั่วหลัก: ครัวทำไปแล้ว (วัตถุดิบหมดไปแล้ว) แต่รายการถูกยกเลิก -->
    <h6 class="fw-bold mb-2">{{ $t('Cancelled Items') }}</h6>
    <div class="row g-3 mb-3">
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Items Cancelled') }}</div>
            <div class="fw-bold fs-4">{{ summary.items_cancelled || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Cancelled Value') }}</div>
            <div class="fw-bold fs-4">{{ formatCurrency(summary.items_cancelled_amount) }}</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="card border-0 shadow-sm h-100 after-kitchen-card">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('After Kitchen Started') }}</div>
            <div class="fw-bold fs-4 text-danger">{{ summary.items_after_kitchen || 0 }}</div>
            <div class="small text-danger fw-semibold">{{ formatCurrency(summary.items_after_kitchen_amount) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Date') }}</th>
                <th>{{ $t('Order No.') }}</th>
                <th>{{ $t('Item') }}</th>
                <th class="text-end">{{ $t('Qty') }}</th>
                <th>{{ $t('Stage') }}</th>
                <th>{{ $t('Cancelled By') }}</th>
                <th>{{ $t('Reason') }}</th>
                <th class="text-end pe-3">{{ $t('Amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!items.length && !loading">
                <td colspan="8" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="(i, idx) in items" :key="idx">
                <td class="ps-3 text-nowrap">{{ i.date }} <small class="text-muted">{{ i.time }}</small></td>
                <td class="fw-medium">{{ i.order_no || '-' }}</td>
                <td>
                  {{ $ln(i) }}
                  <i v-if="i.restocked" class="fa-solid fa-rotate-left text-muted ms-1" :title="$t('Stock returned')"></i>
                </td>
                <td class="text-end">{{ i.qty }}</td>
                <td><span class="badge" :class="stageBadge(i.stage)">{{ $t(stageLabel(i.stage)) }}</span></td>
                <td>{{ i.cancelled_by }}</td>
                <td><span class="badge bg-light text-dark">{{ $t(i.reason) }}</span></td>
                <td class="text-end pe-3 text-danger fw-semibold">{{ formatCurrency(i.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ยกเลิกทั้งบิล -->
    <h6 class="fw-bold mb-2">{{ $t('Cancelled Bills') }}</h6>

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Cancelled') }}</div>
            <div class="fw-bold fs-4 text-danger">{{ summary.total_cancelled || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Cancel Rate') }}</div>
            <div class="fw-bold fs-4 text-warning">{{ (summary.cancel_rate || 0).toFixed(1) }}%</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Lost Revenue') }}</div>
            <div class="fw-bold fs-4 text-danger">{{ formatCurrency(summary.lost_revenue) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Orders Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Date') }}</th>
                <th>{{ $t('Order No.') }}</th>
                <th>{{ $t('Cancelled By') }}</th>
                <th>{{ $t('Reason') }}</th>
                <th class="text-end pe-3">{{ $t('Amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!cancelledOrdersData.length && !loading">
                <td colspan="5" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="c in cancelledOrdersData" :key="c.order_no">
                <td class="ps-3">{{ c.date }}</td>
                <td class="fw-medium">{{ c.order_no }}</td>
                <td>{{ c.cancelled_by }}</td>
                <td><span class="badge bg-light text-dark">{{ c.reason }}</span></td>
                <td class="text-end pe-3 text-danger fw-semibold">{{ formatCurrency(c.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportCancelledOrders',
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Cancelled Orders',
      summary: { total_cancelled: 0, cancel_rate: 0, lost_revenue: 0, items_cancelled: 0, items_cancelled_amount: 0, items_after_kitchen: 0, items_after_kitchen_amount: 0 },
      orders: [],
      items: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.cancelledOrders(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.summary  = payload.summary || { total_cancelled: 0, cancel_rate: 0, lost_revenue: 0, items_cancelled: 0, items_cancelled_amount: 0, items_after_kitchen: 0, items_after_kitchen_amount: 0 };
      this.orders   = payload.orders || [];
      this.items    = payload.items || [];
    },

    // ช่วงที่ยกเลิกเทียบกับครัว (API: cancelledItems()) — ก่อนครัวเริ่ม = ไม่เสียวัตถุดิบ
    stageLabel(stage) {
      return { before_kitchen: 'Before kitchen', cooking: 'While cooking', ready: 'After ready' }[stage] || stage;
    },
    stageBadge(stage) {
      return { before_kitchen: 'bg-secondary', cooking: 'bg-warning text-dark', ready: 'bg-danger' }[stage] || 'bg-secondary';
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Cancelled Items'), style: 'sectionTitle' },
        {
          columns: [
            { text: [{ text: this.$t('Items Cancelled') + '\n', style: 'kpiLabel' }, { text: String(this.summary.items_cancelled || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Cancelled Value') + '\n', style: 'kpiLabel' }, { text: this.formatCurrency(this.summary.items_cancelled_amount), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('After Kitchen Started') + '\n', style: 'kpiLabel' }, { text: `${this.summary.items_after_kitchen || 0} · ${this.formatCurrency(this.summary.items_after_kitchen_amount)}`, style: 'kpiValue', color: '#ef4444' }], width: '*' },
          ],
          margin: [0, 0, 0, 16],
        },
        {
          table: {
            headerRows: 1,
            widths: ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('Date'), style: 'tableHeader' },
                { text: this.$t('Order No.'), style: 'tableHeader' },
                { text: this.$t('Item'), style: 'tableHeader' },
                { text: this.$t('Qty'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Stage'), style: 'tableHeader' },
                { text: this.$t('Cancelled By'), style: 'tableHeader' },
                { text: this.$t('Reason'), style: 'tableHeader' },
                { text: this.$t('Amount'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.items.map(i => [
                { text: `${i.date} ${i.time}`, style: 'tableCell' },
                { text: i.order_no || '-', style: 'tableCell' },
                { text: this.$ln(i), style: 'tableCell' },
                { text: String(i.qty), style: 'tableCell', alignment: 'right' },
                { text: this.$t(this.stageLabel(i.stage)), style: 'tableCell', color: i.stage === 'before_kitchen' ? undefined : '#ef4444' },
                { text: i.cancelled_by, style: 'tableCell' },
                { text: this.$t(i.reason), style: 'tableCell' },
                { text: this.formatCurrencyRaw(i.amount), style: 'tableCell', alignment: 'right', color: '#ef4444' },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 20],
        },
        { text: this.$t('Cancelled Bills'), style: 'sectionTitle' },
        {
          columns: [
            { text: [{ text: this.$t('Total Cancelled') + '\n', style: 'kpiLabel' }, { text: String(this.summary.total_cancelled || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Cancel Rate') + '\n', style: 'kpiLabel' }, { text: `${(this.summary.cancel_rate || 0).toFixed(1)}%`, style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Lost Revenue') + '\n', style: 'kpiLabel' }, { text: this.formatCurrency(this.summary.lost_revenue), style: 'kpiValue' }], width: '*' },
          ],
          margin: [0, 0, 0, 16],
        },
        {
          table: {
            headerRows: 1,
            widths: ['auto', 'auto', 'auto', '*', 'auto'],
            body: [
              [
                { text: this.$t('Date'), style: 'tableHeader' },
                { text: this.$t('Order No.'), style: 'tableHeader' },
                { text: this.$t('Cancelled By'), style: 'tableHeader' },
                { text: this.$t('Reason'), style: 'tableHeader' },
                { text: this.$t('Amount'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.cancelledOrdersData.map(c => [
                { text: c.date, style: 'tableCell' },
                { text: c.order_no, style: 'tableCell' },
                { text: c.cancelled_by, style: 'tableCell' },
                { text: c.reason, style: 'tableCell' },
                { text: this.formatCurrencyRaw(c.amount), style: 'tableCell', alignment: 'right', color: '#ef4444' },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];
      return { content };
    },
  },
  computed: {
    cancelledOrdersData() {
      return this.orders || [];
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.after-kitchen-card {
  border-left: 4px solid #ef4444 !important;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
